import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useApplication } from 'src/context/ApplicationContext';
import { vnFinanzierungDataTransformer } from './vnFinanzierungDataTransformer';

// import schemas
import theme from 'src/assets/theme/theme';
import VerwendungsnachweisSchema from 'src/lib/paths/verwendungsnachweis_FULL_Paths.json';
import { DisplayEuro } from 'src/utils/formats';
const VN_P = VerwendungsnachweisSchema.attributes;

const ZahlenMaesigeNachweisBerichtInvest: FC<{}> = () => {
	const { application } = useApplication();

	const allKostenPosition = useMemo(
		() => get(application, VN_P.Foerderantrag.schema.attributes.Foerderantrag_Kostenpositions.path, []),
		[application]
	);

	const allNachweisPosition = useMemo(() => get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []), [application]);

	const data = useMemo(
		() => vnFinanzierungDataTransformer(allNachweisPosition, allKostenPosition, application),
		[allNachweisPosition, allKostenPosition, application]
	);

	return (
		<>
			<TableContainer>
				<Table aria-label='Ausgaben- und Finanzierungsplan'>
					<TableHead>
						<TableRow>
							<TableCell>Investitionskosten</TableCell>
							<TableCell align='right' width={220}>
								<Typography variant='body1'>SOLL</Typography>
								<Typography variant='body2'>laut aktuellem Ausgabenplan pro Haushaltsjahr</Typography>
							</TableCell>
							<TableCell align='right' width={250}>
								<Typography variant='body1'>IST</Typography>
								<Typography variant='body2'>gemäß Nachweis pro Haushaltsjahr</Typography>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{/* eslint-disable-next-line array-callback-return */}
						{data.rows.map((row, index) => {
							const isDetail = row.type === 'detail';

							if (isDetail) {
								return (
									<TableRow key={`${row.name}-${index}`} /*sx={{ backgroundColor: theme.palette.background.paper }}*/>
										<TableCell>{row.name}</TableCell>
										<TableCell
											align='right'
											sx={{
												fontWeight: 600,
											}}
										>
											{row.bewilligt !== null ? DisplayEuro(row.bewilligt) : '-'}
										</TableCell>
										<TableCell align='right' sx={{ fontWeight: 600 }}>
											{row.mittelabruf !== null ? DisplayEuro(row.mittelabruf) : '-'}
										</TableCell>
									</TableRow>
								);
							}
						})}

						<TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
							<TableCell
								sx={{
									fontWeight: 600,
								}}
							>
								Summe Ausgaben
							</TableCell>
							<TableCell
								align='right'
								sx={{
									fontWeight: 600,
								}}
							>
								{data.rows[0].bewilligt !== null ? DisplayEuro(data.rows[0].bewilligt) : '-'}
							</TableCell>
							<TableCell align='right' sx={{ fontWeight: 600 }}>
								{data.rows[0].mittelabruf !== null ? DisplayEuro(data.rows[0].mittelabruf) : '-'}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

const ZahlenMaesigeNachweisBerichtFinanzierung: FC<{
	sollEigenmittel: number;
	isSummeEigenmittel: number;
	isSummeFinanzierung: number;
}> = ({ sollEigenmittel, isSummeEigenmittel, isSummeFinanzierung }) => {
	const { application } = useApplication();

	const allKostenPosition = useMemo(
		() => get(application, VN_P.Foerderantrag.schema.attributes.Foerderantrag_Kostenpositions.path, []),
		[application]
	);

	const allNachweisPosition = useMemo(() => get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []), [application]);

	const data = useMemo(
		() => vnFinanzierungDataTransformer(allNachweisPosition, allKostenPosition, application),
		[allNachweisPosition, allKostenPosition, application]
	);

	return (
		<>
			<TableContainer>
				<Table aria-label='Ausgaben- und Finanzierungsplan'>
					<TableHead>
						<TableRow>
							<TableCell>Finanzierung</TableCell>
							<TableCell align='right' width={220}>
								<Typography variant='body1'>SOLL</Typography>
								<Typography variant='body2'>laut aktuellem Finanzierungsplan pro Haushaltsjahr</Typography>
							</TableCell>
							<TableCell align='right' width={250}>
								<Typography variant='body1'>IST</Typography>
								<Typography variant='body2'>gemäß Nachweis pro Haushaltsjahr</Typography>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Eigen- und Drittmittel</TableCell>
							<TableCell
								align='right'
								sx={{
									fontWeight: 600,
								}}
							>
								{sollEigenmittel !== null ? DisplayEuro(sollEigenmittel) : '-'}
							</TableCell>
							<TableCell align='right' sx={{ fontWeight: 600 }}>
								{isSummeEigenmittel !== null ? DisplayEuro(isSummeEigenmittel) : '-'}
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Zuwendung anteilig (gemäß Förderquote)</TableCell>
							<TableCell
								align='right'
								sx={{
									fontWeight: 600,
								}}
							>
								{data.bewilligteFoerderung !== null ? DisplayEuro(data.bewilligteFoerderung) : '-'}
							</TableCell>
							<TableCell align='right' sx={{ fontWeight: 600 }}>
								{data.gesamtMaxFoerderung !== null ? DisplayEuro(data.gesamtMaxFoerderung) : '-'}
							</TableCell>
						</TableRow>

						<TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
							<TableCell
								sx={{
									fontWeight: 600,
								}}
							>
								Summe Finanzierung
							</TableCell>
							<TableCell
								align='right'
								sx={{
									fontWeight: 600,
								}}
							>
								{data.rows[0].bewilligt !== null ? DisplayEuro(data.rows[0].bewilligt) : '-'}
							</TableCell>
							<TableCell align='right' sx={{ fontWeight: 600 }}>
								{isSummeFinanzierung !== null ? DisplayEuro(isSummeFinanzierung) : '-'}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				<strong>Bitte beachten Sie:</strong> Ausgaben können nur bis zur im Förderbescheid festgesetzten Ausgabenhöhe als zuwendungsfähig
				anerkannt werden können. Mögliche Mehrausgaben gehen zu Lasten des Zuwendungsempfängers. Bitte berücksichtigen Sie dies bei der Angabe der
				Eigen- und Drittmittel
			</Typography>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				Der Verwendungsnachweis wurde automatisch durch das digitale Antragsportal der Innovationsförderung Hessen – basierend auf den von Ihnen
				gemachten Angaben – generiert und steht unter dem Vorbehalt einer fachlichen Prüfung durch die Hessen Agentur.
			</Typography>
		</>
	);
};

export { ZahlenMaesigeNachweisBerichtFinanzierung, ZahlenMaesigeNachweisBerichtInvest };
